/**
 * `RequireAuth` — shared auth-presence guard for customer-portal and
 * admin-portal route trees.
 *
 * Renders `<Outlet />` when Redux `auth.isAuthenticated` is true.
 * Otherwise redirects to `fallbackPath` (caller-supplied — no hardcoded
 * `/login` in the capsule) with `state.from` set to the requested
 * location so the login view can return the user to their original
 * target after a successful sign-in.
 *
 * Boot phase gate (SF-AUTH-PERSIST): while `auth.bootPhase === 'pending'`
 * the component renders `null` — `bootstrapAuth` is still attempting a
 * silent cookie refresh and we must not bounce the user to /login prematurely.
 * Once the refresh attempt settles (success or failure), `bootPhase` becomes
 * `'ready'` and the guard evaluates `isAuthenticated` normally.
 *
 * Historically this was an SF-INFRA-002 stub that rendered the Outlet
 * unconditionally, and the Console shipped a local copy of the real
 * guard. SH-AUTH-H004 consolidates both into this capsule.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAppSelector } from '@dfq/shared/hooks/useStore';

export interface RequireAuthProps {
  /**
   * Route the unauthenticated user is redirected to. The caller supplies
   * this — keeps this capsule app-neutral so the storefront can point at
   * `/login` while the console can point at `/admin/login` (or whatever
   * the app's login path ends up being).
   */
  fallbackPath?: string;
  /**
   * When `true`, the redirect target carries the original location as a
   * `?returnUrl=...` query parameter (URL-encoded `pathname + search +
   * hash`). The Storefront customer-portal expects this shape (per
   * SF-ACCT-001 acceptance criteria) so the login view can read
   * `searchParams.get('returnUrl')` and post-login route the user back.
   *
   * Defaults to `false` — the legacy `state.from` shape is still emitted
   * for back-compat with the Console flow that consumes it.
   */
  useReturnUrlParam?: boolean;
}

export function RequireAuth({
  fallbackPath = '/login',
  useReturnUrlParam = false,
}: RequireAuthProps = {}) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const bootPhase = useAppSelector((state) => state.auth.bootPhase);
  const location = useLocation();

  // SF-AUTH-PERSIST: hold until the cookie refresh attempt settles.
  if (bootPhase === 'pending') {
    return null;
  }

  if (!isAuthenticated) {
    if (useReturnUrlParam) {
      const target = `${location.pathname}${location.search}${location.hash}`;
      const separator = fallbackPath.includes('?') ? '&' : '?';
      const redirectTo = `${fallbackPath}${separator}returnUrl=${encodeURIComponent(target)}`;
      return <Navigate to={redirectTo} replace />;
    }
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return <Outlet />;
}

export default RequireAuth;
